import React, { useEffect, useState } from "react";
import PageLoader from "../../../shared/PageLoader";
import { useAppDispatch, useAppSelector } from "../../../../redux/hook";

import Select from "react-select";
import PerformerDropdownIndicator from "../shared/PerformerDropdownIndicator";
import Modal from "react-bootstrap/Modal";

import { IoMdClose } from "react-icons/io";
import Icon from "../../../common/Icon";
import { downloadWithPresignedUrl } from "../../../../utils/common";
import {
  ERROR_MESSAGES,
  ESTABLISHMENT_TYPE,
} from "../../../../utils/constants";
import { toast } from "react-toastify";
import { hasUserAccess } from "../../../../backend/auth";
import { USER_PRIVILEGES } from "../../UserManagement/Roles/RoleConfiguration";
import { addStateRegisterQue, checkRegisterDataAvailability, checkClraDataAvailability } from "../../../../redux/features/stateRegisterQue.slice";

import {
  useFilters,
  useSearchConfig,
  useStateRegisterData,
  useCompanyData,
  useEmployerData,
} from "../../../../hooks/usePerformerClientRegisters";
import {
  formatDate,
  generateYearsList,
  MONTH_LIST,
  getResetFields,
} from "../../../../utils/performerClientRegisters.utils";

import PerformerTable from "../shared/PerformerTable";
import EmployeeUploadSearch from "../../../common/EmployeeUploadSearch";
import DownloadModal from "./DownloadModal";
import "../shared/performer-client.css";
import { useAssociateCompanyLogo } from "../../../../contexts/AssociateCompanyContext";


const RegistersQueue = () => {
  const dispatch: any = useAppDispatch();
  const stateRegisterQueDetails = useAppSelector(
    (state) => state.stateRegisterQue.stateRegisterQueDetails,
  );
  const stateRegisterQueExcelandPdfDetails = useAppSelector(
    (state) =>
      state.stateRegisterQue.stateRegisterQueExcelandPdfDownloadDetails,
  );

  const QUE_STATUS_DISPLAY: Record<string, string> = {
    pending: 'Pending',
    processing: 'In Progress',
    excel_processing: 'In Progress',
    pdf_processing: 'In Progress',
    pdf_processing_partially_completed: 'Partially Completed',
    pdf_processing_completed: 'Completed',
    failed: 'Failed'
  };


  // Use custom hooks
  const {
    filters,
    updateFilter,
    updateMultipleFilters,
    resetFilters,
    setFilters,
  } = useFilters();
  const { searchConfig, updateSearchConfig } = useSearchConfig();
  const { fetchData, fetchWithSearch } = useStateRegisterData(
    filters,
    searchConfig,
  );
  const {
    companies,
    associateCompanies,
    locations,
    totalStates,
    fetchCompanies,
    fetchAssociateCompanies,
    fetchLocations,
    fetchStates,
    isFetching: fetchingCompanies,
  } = useCompanyData();
  const { setAssociateCompanyBranding } = useAssociateCompanyLogo();
  const {
    employers,
    employerLocations,
    employerSubContractors,
    employerSubContractorsLocations,
    fetchEmployers,
    fetchEmployerLocations,
    fetchSubContractors,
    fetchSubContractorLocations,
  } = useEmployerData();

  // Extract states from locations
  let states =
    (locations || [])
      .filter((each: any) => each.state)
      .map((each: any) => {
        return { name: each.state.name, id: each.state.id };
      })
      .filter((each: any, index: any, self: any) => {
        return index === self.findIndex((t: any) => t.id === each.id);
      })
      .sort((a: any, b: any) => (a.name || "").localeCompare(b.name || "")) || [];

  // Local state for UI components
  const [downloading, setDownloading] = useState(false);
  const [openDownloadModal, setOpenDownloadModal] = useState(false);
  const [selectedStateRegisterQue, setSelectedStateRegisterQue] = useState<any>(
    {},
  );
  const [stateRegisters, setStateRegisters] = useState<any[]>([]);
  const [showDashboardDetails, setShowDashboardDetails] = useState(true);
  const [openMoreFilters, setOpenMoreFilters] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);

  // Nil-register data-availability check (Shops/Factory, single location only).
  // `checkedFor` fingerprints the exact selection the result applies to, so a
  // stale result never gets applied to a since-changed selection.
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityResult, setAvailabilityResult] = useState<{
    hasData: boolean;
    errors: string[];
    checkedFor: string;
  } | null>(null);

  // Nil-register data-availability check — CLRA. Only acted on when EVERY
  // matching Company/Employer/Sub-Contractor combination for the current
  // selection has has_data:false — generate_nil_register applies to every
  // combination a request matches, so it's only safe to set it when none of
  // the matched combinations actually have data (a mix of some-have/some-don't
  // is left alone; narrowing further via Sub Contractor/Sub Contractor
  // Location resolves that).
  const [checkingClraAvailability, setCheckingClraAvailability] = useState(false);
  const [clraAvailabilityResult, setClraAvailabilityResult] = useState<{
    hasData: boolean;
    errors: string[];
    combinationCount: number;
    checkedFor: string;
  } | null>(null);

  // Nil-register for the "no Sub Contractor" combination specifically — when
  // Sub Contractor is left blank and this Employer/Employer Location has a
  // MIX of combinations (some with a Sub Contractor that has data, one with
  // no Sub Contractor that doesn't), the check above correctly refuses to
  // offer nil (it would wipe the combination that has real data). This
  // second, narrower check targets only the no-Sub-Contractor row via the
  // no_sub_contractor flag, so that one row can be nil-registered without
  // touching its siblings.
  const [checkingClraNoSubAvailability, setCheckingClraNoSubAvailability] = useState(false);
  const [clraNoSubAvailabilityResult, setClraNoSubAvailabilityResult] = useState<{
    hasData: boolean;
    errors: string[];
    checkedFor: string;
  } | null>(null);


  // Redux selectors
  const formsDetails = useAppSelector(
    (state) => state.performerClient.formsDetails,
  );
  const addStateRegisterDetails = useAppSelector(
    (state) => state.stateRegister.addStateRegisterDetails,
  );
  const updateStateRegisterDetails = useAppSelector(
    (state) => state.stateRegister.updateStateRegisterDetails,
  );
  const deleteStateRegisterMappingDetails = useAppSelector(
    (state) => state.stateRegister.deleteStateRegisterMappingDetails,
  );
  const importStateRegisterMappingDetails = useAppSelector(
    (state) => state.stateRegister.importStateRegisterMappingDetails,
  );

  // Data from selectors
  const stateRegisterQue = stateRegisterQueDetails.data.items;
  const stateRegisterQueCount = stateRegisterQueDetails.data.meta.total_count;

  // Check if CLRA columns should be shown (CLRA filter selected OR search active)
  const showCLRAColumns =
    filters.establishmentType === "CLRA" || isSearchActive;

  // Loading state
  const loading =
    importStateRegisterMappingDetails.status === "loading" ||
    deleteStateRegisterMappingDetails.status === "loading" ||
    updateStateRegisterDetails.status === "loading" ||
    addStateRegisterDetails.status === "loading" ||
    formsDetails.status === "loading" ||
    stateRegisterQueDetails.status === "loading";

  const handleChangeCompany = (option: any) => {
    const selectedCompanyId = option ? option.value : "";
    updateMultipleFilters({
      company: selectedCompanyId,
      associateCompany: "",
      stateName: "",
      locations: [],
      employer: "",
      employerLocation: "",
      subContractor: "",
      subContractorLocation: "",
    });
    updateSearchConfig({ page: 0 });

    // Associate Company resets to "All" above — "All" shows the parent
    // company's own branding.
    const company = companies.find((c: any) => c.id === selectedCompanyId);
    setAssociateCompanyBranding(company || null);

    fetchData({
      ...filters,
      company: selectedCompanyId,
      associateCompany: "",
      stateName: "",
      locations: [],
      employer: "",
      employerLocation: "",
      subContractor: "",
      subContractorLocation: "",
    }, { page: 0 }, {
      que_id_eq: searchConfig.searchInput,
    });

    if (selectedCompanyId) {
      fetchAssociateCompanies(selectedCompanyId);
    }
  };

  // Removed: Using generic getNameById from utils instead

  const handleChangeAssociateCompany = (option: any) => {
    const selectedCompanyId = option ? option.value : "";
    if (selectedCompanyId) {
      const ac = associateCompanies.find((a: any) => a.id === selectedCompanyId);
      setAssociateCompanyBranding(ac || null);
    } else {
      // "All" shows the parent company's own branding.
      const company = companies.find((c: any) => c.id === filters.company);
      setAssociateCompanyBranding(company || null);
    }
    updateMultipleFilters({
      associateCompany: selectedCompanyId,
      stateName: "",
      locations: [],
      employer: "",
      employerLocation: "",
      subContractor: "",
      subContractorLocation: "",
    });
    updateSearchConfig({ page: 0 });

    fetchData({
      ...filters,
      associateCompany: selectedCompanyId,
      stateName: "",
      locations: [],
      employer: "",
      employerLocation: "",
      subContractor: "",
      subContractorLocation: "",
    }, { page: 0 });

    if (selectedCompanyId) {
      fetchLocations(selectedCompanyId);
      fetchStates();
    }
  };

  // Removed: Using generic getNameById from utils instead

  const handleChangeStateName = (option: any) => {
    const selectedStateId = option ? option.value : "";
    // Only Location depends on State — Year/Month should persist across a
    // State change instead of getting reset.
    updateMultipleFilters({ stateName: selectedStateId, locations: [] });
    updateSearchConfig({ page: 0 });
    fetchData({ ...filters, stateName: selectedStateId, locations: [] }, { page: 0 });
  };

  const handleChangeLocationMulti = (options: any) => {
    const selectedLocations = (options || []).map((o: any) => o.value);
    // Use updateMultipleFilters (not updateFilter) — updateFilter runs the
    // generic cascade reset for "locations", which also clears Year/Month.
    // Changing which locations are selected shouldn't reset those.
    updateMultipleFilters({ locations: selectedLocations });
    updateSearchConfig({ page: 0 });
    fetchData({ ...filters, locations: selectedLocations }, { page: 0 });
    if (selectedLocations && selectedLocations.length > 0) {
      fetchEmployers({ ...filters, locations: selectedLocations }, locations);
    }
  };

  const handleChangeLocationSingle = (option: any) => {
    const selectedLocations = option && option.value ? [option.value] : [];
    updateMultipleFilters({
      locations: selectedLocations,
      employer: "",
      employerLocation: "",
      subContractor: "",
      subContractorLocation: "",
    });
    updateSearchConfig({ page: 0 });
    fetchData({
      ...filters,
      locations: selectedLocations,
      employer: "",
      employerLocation: "",
      subContractor: "",
      subContractorLocation: "",
    }, { page: 0 });
    if (selectedLocations && selectedLocations.length > 0) {
      fetchEmployers({ ...filters, locations: selectedLocations }, locations);
    }
  };

  const handleChangeYear = (option: any) => {
    const selectedYear = option ? option.value : "";
    updateFilter("year", selectedYear);
    updateSearchConfig({ page: 0 });

    fetchData({ ...filters, year: selectedYear, month: "" }, { page: 0 });
  };

  const handleChangeMonth = (option: any) => {
    const selectedMonth = option ? option.value : "";
    updateFilter("month", selectedMonth);
    updateSearchConfig({ page: 0 });

    fetchData({ ...filters, month: selectedMonth }, { page: 0 });
  };

  const handleChangeEstablishmentType = (option: any) => {
    const selectedType = option ? option.value : "";
    setIsSearchActive(false);
    updateFilter("establishmentType", selectedType);
    updateSearchConfig({ page: 0 });
    const resetFields = getResetFields("establishmentType");
    const cleanFilters = {
      company: "",
      associateCompany: "",
      stateName: "",
      locations: [],
      year: "",
      month: "",
      employer: "",
      employerLocation: "",
      subContractor: "",
      subContractorLocation: "",
      ...resetFields,
      establishmentType: selectedType,
    };

    fetchData(cleanFilters, { page: 0 }, { que_id_eq: searchConfig.searchInput });
  };

  // Initialize data on component mount
  useEffect(() => {
    fetchCompanies();
    const params = new URLSearchParams(window.location.search);
    const queId = params.get("queId");
    if (queId) {
      updateSearchConfig({ searchInput: queId });
      setIsSearchActive(true);
      fetchWithSearch(queId);
    } else {
      fetchData();
    }
  }, []);

  // No "All" for the Company filter — auto-select the first company
  // alphabetically once the list resolves. Reuses handleChangeCompany so the
  // usual cascade reset/fetch/fetchAssociateCompanies behavior applies.
  useEffect(() => {
    if (filters.company) return;
    if (!companies || companies.length === 0) return;
    const sorted = [...companies].sort((a: any, b: any) => (a.name || "").localeCompare(b.name || ""));
    handleChangeCompany({ value: sorted[0].id, label: sorted[0].name });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, filters.company]);

  // Fetch associate companies when company changes
  useEffect(() => {
    if (filters.company) {
      fetchAssociateCompanies(filters.company);
    }
  }, [filters.company]);

  // No "All" option for Company — it keeps its own auto-select-first-
  // alphabetically behavior, unchanged. Only resolve to "nothing selectable"
  // (default logo) once loading has genuinely finished and found zero
  // companies — never just because nothing's picked yet, or the default
  // ezyCOMP logo flashes before the real one loads.
  // filters.company MUST be a dependency here (even though it's also read in
  // the guard) — Establishment Type changes reset it back to '' via a
  // completely separate update (handleChangeEstablishmentType), and without
  // filters.company listed, this effect has no way to notice that reset:
  // companies/fetchingCompanies don't change just because establishmentType
  // did, so the effect would silently never re-fire.
  useEffect(() => {
    if (filters.company || fetchingCompanies) return;
    if (companies.length === 0) {
      setAssociateCompanyBranding(null);
      return;
    }
    const sorted = [...companies].sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));
    handleChangeCompany({ value: sorted[0].id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, fetchingCompanies, filters.company]);

  // Associate Company has "All" back as its resting value (filters.associateCompany
  // stays "") — no more auto-select of a specific associate company. "All"
  // shows the parent company's own branding, applied inside handleChangeCompany
  // (whenever Company resolves or changes) and inside handleChangeAssociateCompany
  // (whenever "All" is re-selected).

  // Fetch locations when associate company changes
  useEffect(() => {
    if (filters.associateCompany) {
      fetchLocations(filters.associateCompany);
      fetchStates();
    }
  }, [filters.associateCompany]);

  // Removed: Consolidated with main useEffect above

  // Removed: Consolidated with filters.company useEffect

  // Removed: Consolidated with filters.associateCompany useEffect

  useEffect(() => {
    if (stateRegisterQueDetails.status === "succeeded") {
      //alert("this success  was hitted");
    } else if (stateRegisterQueDetails.status === "failed") {
      toast.error(ERROR_MESSAGES.DEFAULT);
    }
  }, [stateRegisterQueDetails.status]);

  // Fetch employer-related data when filters change
  useEffect(() => {
    if (filters.locations && filters.locations.length > 0) {
      fetchEmployers(filters, locations);
    }
  }, [filters.locations]);

  useEffect(() => {
    if (filters.employer) {
      fetchEmployerLocations(filters, locations);
    }
  }, [filters.employer]);

  useEffect(() => {
    if (filters.employerLocation) {
      fetchSubContractors(filters, locations);
    }
  }, [filters.employerLocation]);

  useEffect(() => {
    if (filters.subContractor) {
      fetchSubContractorLocations(filters, locations);
    }
  }, [filters.subContractor]);

  // Nil-register data-availability check — Shops/Factory only, and only when
  // exactly one location is selected (the single-location flow). Re-runs
  // whenever any part of the selection changes.
  const singleLocationKey = filters.locations.length === 1 ? filters.locations[0] : "";
  useEffect(() => {
    if (
      filters.establishmentType === "CLRA" ||
      filters.locations.length !== 1 ||
      !filters.company ||
      !filters.associateCompany ||
      !filters.stateName ||
      !filters.year ||
      !filters.month
    ) {
      setAvailabilityResult(null);
      return;
    }
    const checkedFor = [
      filters.company, filters.associateCompany, filters.stateName,
      singleLocationKey, filters.establishmentType, filters.year, filters.month,
    ].join("|");
    let cancelled = false;
    setCheckingAvailability(true);
    setAvailabilityResult(null); // clear any stale result while this selection's check is in flight
    dispatch(
      checkRegisterDataAvailability({
        company_id: filters.company,
        associate_company_id: filters.associateCompany,
        state_id: filters.stateName,
        location_id: singleLocationKey,
        establishment_type: filters.establishmentType,
        year: filters.year,
        month: filters.month,
      }),
    ).then((resultAction: any) => {
      if (cancelled) return;
      if (checkRegisterDataAvailability.fulfilled.match(resultAction)) {
        const data = resultAction.payload.data || {};
        const hasData = data.has_data !== false;
        setAvailabilityResult({ hasData, errors: data.errors || [], checkedFor });
        if (!hasData) {
          const msg = data.errors && data.errors.length
            ? data.errors.join(", ")
            : "No data available for this location for the selected period.";
          toast.warn(`${msg} Generate Registers will create a Nil register.`);
        }
      } else {
        // Check failed — proceed as normal (safe default), no nil register flag.
        setAvailabilityResult({ hasData: true, errors: [], checkedFor });
      }
    }).finally(() => {
      if (!cancelled) setCheckingAvailability(false);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.establishmentType, filters.company, filters.associateCompany,
    filters.stateName, singleLocationKey, filters.year, filters.month,
  ]);

  // Nil-register data-availability check — CLRA. check_clra_data_availability
  // returns one entry per matching Company/Employer/Sub-Contractor combination
  // for the current filters. generate_nil_register applies to every
  // combination a request matches, so it's only safe to act on this when the
  // every matched combination actually lacks data — only fire
  // once Company, Associate Company, Location, Employer, Employer Location,
  // Year and Month are all selected (Sub Contractor / Sub Contractor Location
  // stay optional narrowing on top of that).
  useEffect(() => {
    if (
      filters.establishmentType !== "CLRA" ||
      !filters.company ||
      !filters.associateCompany ||
      filters.locations.length !== 1 ||
      !filters.employer ||
      !filters.employerLocation ||
      !filters.year ||
      !filters.month
    ) {
      setClraAvailabilityResult(null);
      setClraNoSubAvailabilityResult(null);
      return;
    }
    const checkedFor = [
      filters.company, filters.associateCompany, filters.year, filters.month,
      filters.locations[0] || "", filters.employer, filters.employerLocation,
      filters.subContractor, filters.subContractorLocation,
    ].join("|");
    let cancelled = false;
    const params: any = {
      company_id: filters.company,
      associate_company_id: filters.associateCompany,
      establishment_type: filters.establishmentType,
      year: filters.year,
      month: filters.month,
    };
    if (filters.locations[0]) {
      const loc = locations.find((l: any) => l.location_id === filters.locations[0]);
      if (loc) {
        params.location_id = loc.location_id;
        params.company_location_mapping_id = loc.company_location_mapping_id;
      }
    }
    if (filters.employer) params.employer_id = filters.employer;
    if (filters.employerLocation) params.employer_location_id = filters.employerLocation;
    if (filters.subContractor) params.sub_contractor_id = filters.subContractor;
    if (filters.subContractorLocation) params.sub_contractor_location_id = filters.subContractorLocation;

    async function runClraCheck() {
      setCheckingClraAvailability(true);
      // Clear any stale results while this selection's check is in flight.
      setClraAvailabilityResult(null);
      setClraNoSubAvailabilityResult(null);

      const resultAction = await dispatch(checkClraDataAvailability(params));
      if (cancelled) return;

      if (!checkClraDataAvailability.fulfilled.match(resultAction)) {
        // Check failed — proceed as normal (safe default), no nil register flag.
        setCheckingClraAvailability(false);
        return;
      }

      const combinations = (resultAction.payload.data || {}).combinations || [];
      const allNoData = combinations.length > 0 && combinations.every((c: any) => c.has_data === false);

      if (allNoData) {
        const errors = Array.from(new Set(combinations.flatMap((c: any) => c.errors || []))) as string[];
        setClraAvailabilityResult({ hasData: false, errors, combinationCount: combinations.length, checkedFor });
        const msg = errors.length
          ? errors.join(", ")
          : "No data available for the selected combination(s).";
        const registerWord = combinations.length > 1 ? `${combinations.length} Nil registers` : "a Nil register";
        toast.warn(`${msg} Generate Registers will create ${registerWord}.`);
        setCheckingClraAvailability(false);
        return;
      }

      // Mixed results (some combinations have data, some don't) — not safe to
      // blanket-apply generate_nil_register across all of them. But if the
      // specific "no Sub Contractor" combination among them lacks data, it can
      // still be nil-registered on its own via the no_sub_contractor flag,
      // without touching its siblings that do have a Sub Contractor and data.
      const hasMixedResults = combinations.length > 1
        && combinations.some((c: any) => c.has_data === false)
        && combinations.some((c: any) => c.has_data !== false);
      const noSubEntry = combinations.find((c: any) => !c.sub_contractor_id);
      setCheckingClraAvailability(false);
      if (!hasMixedResults || !noSubEntry || noSubEntry.has_data !== false) {
        return;
      }

      setCheckingClraNoSubAvailability(true);
      const noSubResultAction = await dispatch(
        checkClraDataAvailability({ ...params, no_sub_contractor: true }),
      );
      if (cancelled) { setCheckingClraNoSubAvailability(false); return; }
      if (checkClraDataAvailability.fulfilled.match(noSubResultAction)) {
        const noSubCombinations = (noSubResultAction.payload.data || {}).combinations || [];
        if (noSubCombinations.length === 1 && noSubCombinations[0].has_data === false) {
          const combo = noSubCombinations[0];
          setClraNoSubAvailabilityResult({ hasData: false, errors: combo.errors || [], checkedFor });
          const msg = combo.errors && combo.errors.length
            ? combo.errors.join(", ")
            : "No data available for this combination without a Sub Contractor.";
          toast.warn(`${msg} Generate Registers will create a Nil register for this combination only.`);
        }
      }
      setCheckingClraNoSubAvailability(false);
    }

    runClraCheck();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.establishmentType, filters.company, filters.associateCompany,
    filters.year, filters.month, filters.locations, filters.employer,
    filters.employerLocation, filters.subContractor, filters.subContractorLocation,
  ]);

  function handleChangeEmployer(option: any) {
    const selectedEmployer = option ? option.value : "";
    updateMultipleFilters({
      employer: selectedEmployer,
      employerLocation: "",
      subContractor: "",
      subContractorLocation: "",
    });
    updateSearchConfig({ page: 0 });

    fetchData({
      ...filters,
      employer: selectedEmployer,
      employerLocation: "",
      subContractor: "",
      subContractorLocation: "",
    }, { page: 0 });

    if (selectedEmployer) {
      fetchEmployerLocations(
        { ...filters, employer: selectedEmployer },
        locations,
      );
    }
  }

  function handleChangeEmployerLocation(option: any) {
    const selectedLocation = option ? option.value : "";
    updateMultipleFilters({
      employerLocation: selectedLocation,
      subContractor: "",
      subContractorLocation: "",
    });
    updateSearchConfig({ page: 0 });

    fetchData({
      ...filters,
      employerLocation: selectedLocation,
      subContractor: "",
      subContractorLocation: "",
    }, { page: 0 });

    if (selectedLocation) {
      fetchSubContractors(
        { ...filters, employerLocation: selectedLocation },
        locations,
      );
    }
  }

  function handleChangeSubContractor(event: any) {
    const selectedSubContractor = event.value as string;
    updateFilter("subContractor", selectedSubContractor);

    if (selectedSubContractor) {
      fetchSubContractorLocations(
        { ...filters, subContractor: selectedSubContractor },
        locations,
      );
    }
  }

  function handleChangeSubContractorLocation(event: any) {
    const selectedLocation = event.value as string;
    updateFilter("subContractorLocation", selectedLocation);
  }

  function applyMoreFilters() {
    setOpenMoreFilters(false);
    updateSearchConfig({ page: 0 });
    fetchData(undefined, { page: 0 });
  }

  function clearMoreFilters() {
    updateMultipleFilters({
      subContractor: "",
      subContractorLocation: "",
    });
    setTimeout(() => {
      setOpenMoreFilters(false);
      updateSearchConfig({ page: 0 });
      fetchData({ ...filters, subContractor: "", subContractorLocation: "" }, { page: 0 });
    }, 100);
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    updateSearchConfig({ page: newPage });
    fetchData(undefined, { page: newPage });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    updateSearchConfig({ rowsPerPage: newRowsPerPage, page: 0 });
    fetchData(undefined, { rowsPerPage: newRowsPerPage, page: 0 });
  };

  const handleSort = (sortKey: string) => {
    const newSortType = searchConfig.activeSort === sortKey && searchConfig.sortType === "asc" ? "desc" : "asc";
    updateSearchConfig({ activeSort: sortKey, sortType: newSortType });
    fetchData(filters, undefined, {}, sortKey, newSortType);
  };

  // Use utility functions for lists
  const yearsList = generateYearsList();
  const monthList = MONTH_LIST;
  const EstablishmentTypesList = Object.values(ESTABLISHMENT_TYPE);

  // react-select options
  const ALL_OPTION = { value: '', label: 'All' };
  const establishmentTypeOptions = [ALL_OPTION, ...EstablishmentTypesList.map((each) => ({ value: each, label: each }))];
  // No "All" for Company — sorted alphabetically since it's now auto-selected
  // to the first one (see the auto-select effect above).
  const companyOptions = [...companies]
    .sort((a: any, b: any) => (a.name || "").localeCompare(b.name || ""))
    .map((c: any) => ({ value: c.id, label: c.name }));
  const associateCompanyOptions = [ALL_OPTION, ...associateCompanies.map((c: any) => ({ value: c.id, label: c.name }))];
  const stateOptions = [ALL_OPTION, ...states.map((s: any) => ({ value: s.id, label: s.name }))];
  const sortLocationsByStateAndName = (a: any, b: any) => {
    const stateCmp = ((a.state && a.state.name) || "").localeCompare((b.state && b.state.name) || "");
    if (stateCmp !== 0) return stateCmp;
    return (a.name || "").localeCompare(b.name || "");
  };
  const nonClraLocationOptions = (locations || [])
    .filter((each: any) => {
      return each.location_id && each.state && each.state.id === filters.stateName;
    })
    .slice()
    .sort(sortLocationsByStateAndName)
    .map((each: any) => {
      const { name, code, city, state } = each;
      return { value: each.location_id, label: `${name} (${state.code}-${(city || {}).code}-${code})` };
    });
  const clraLocationOptions = (locations || [])
    .filter((each: any) => {
      return each.location_id && each.state;
    })
    .slice()
    .sort(sortLocationsByStateAndName)
    .map((each: any) => {
      const { name, code, city, state } = each;
      return { value: each.location_id, label: `${name} (${state.code}-${(city || {}).code}-${code})` };
    });
  const employerOptions = [ALL_OPTION, ...(employers || []).map((e: any) => ({ value: e.id, label: e.name }))];
  const employerLocationOptions = [ALL_OPTION, ...(employerLocations || []).map((e: any) => ({ value: e.id, label: e.location_name }))];
  const yearOptions = [ALL_OPTION, ...yearsList.map((y: any) => ({ value: y, label: String(y) }))];
  const monthOptions = [ALL_OPTION, ...monthList.map((m: any) => ({ value: m, label: m }))];

  // react-select selected values
  const selectedEstablishmentType = filters.establishmentType ? { value: filters.establishmentType, label: filters.establishmentType } : ALL_OPTION;
  const selectedCompany = (filters.company && companyOptions.find((o: any) => o.value === filters.company)) || null;
  const selectedAssociateCompany = (filters.associateCompany && associateCompanyOptions.find((o: any) => o.value === filters.associateCompany)) || ALL_OPTION;
  const selectedStateName = (filters.stateName && stateOptions.find((o: any) => o.value === filters.stateName)) || ALL_OPTION;
  const selectedLocationsMulti = filters.locations.map((id: string) => nonClraLocationOptions.find((o: any) => o.value === id)).filter(Boolean);
  const selectedLocationSingle = (filters.locations.length > 0 && clraLocationOptions.find((o: any) => o.value === filters.locations[0])) || ALL_OPTION;
  const selectedEmployer = (filters.employer && employerOptions.find((o) => o.value === filters.employer)) || ALL_OPTION;
  const selectedEmployerLocation = (filters.employerLocation && employerLocationOptions.find((o) => o.value === filters.employerLocation)) || ALL_OPTION;
  const selectedYear = filters.year ? { value: filters.year, label: String(filters.year) } : ALL_OPTION;
  const selectedMonth = filters.month ? { value: filters.month, label: filters.month } : ALL_OPTION;

  // Backend no longer returns per-location/per-employer detail in any
  // response — that breakdown always goes out by email to the triggering
  // user. Every path here just surfaces the backend's own `message` (or an
  // `errors[]`/`error` on failure), it never reconstructs per-item detail.
  const buildErrorMessage = (payload: any): React.ReactNode => {
    const errorData = payload || {};
    if (Array.isArray(errorData.errors) && errorData.errors.length > 0) {
      return (
        <div>
          {errorData.errors.map((err: string, i: number) => (
            <div key={i}>{err}</div>
          ))}
        </div>
      );
    }
    return errorData.error || errorData.message || "An error occurred while processing the registers.";
  };

  // Nil registers can only be requested for a single, unambiguous selection
  // (one location for Shops/Factory, one resolved combination for CLRA) — so
  // whenever the backend's raw "no data available" message comes back for a
  // broader/ambiguous selection, replace it with copy that tells the user
  // what to do about it instead of just repeating the raw error. Applies to
  // every branch (CLRA and Shops/Factory alike).
  const NO_DATA_MESSAGE_PATTERN = /no data available to proceed for register generation/i;
  const NO_DATA_FRIENDLY_MESSAGE = "No data uploaded for the selected locations. Select only one location at a time to generate the NIL registers.";
  const NO_DATA_FRIENDLY_MESSAGE_CLRA = "No data uploaded for the selected location. Select all the filters to generate the NIL register.";
  const getFriendlyErrorMessage = (payload: any, isClra: boolean = false): React.ReactNode => {
    const errorData = payload || {};
    const rawMessage = String(errorData.error || errorData.message || "");
    if (NO_DATA_MESSAGE_PATTERN.test(rawMessage)) {
      return isClra ? NO_DATA_FRIENDLY_MESSAGE_CLRA : NO_DATA_FRIENDLY_MESSAGE;
    }
    return buildErrorMessage(payload);
  };
  // A "0 created" response is technically a success (HTTP-wise) but means the
  // same thing as the no-data error above for a broad/ambiguous CLRA
  // selection — surface it the same way instead of a green success toast.
  const isZeroCreatedMessage = (data: any): boolean => {
    if (!data) return false;
    if (data.created_count === 0 || data.count === 0) return true;
    return /(^|\D)0\s+state register queue/i.test(String(data.message || ""));
  };

  const handleProcessRegisters = async () => {
    setDownloading(true);
    try {
      const establishmentType = filters.establishmentType;

      // ── CLRA — Location/Employer/Sub Contractor are all optional. The
      // backend sizes the request by how many Company/Employer/Sub-Contractor
      // combinations match whatever filters were given, then decides sync
      // (≤50 matches — counts) vs async (51+ — background + email) itself. ──
      if (establishmentType === "CLRA") {
        const payload: any = {
          state_register_que: {
            company_id: filters.company,
            associate_company_id: filters.associateCompany,
            year: filters.year,
            month: filters.month,
            establishment_type: establishmentType,
          },
        };
        if (filters.locations[0]) {
          const loc = locations.find((l: any) => l.location_id === filters.locations[0]);
          if (loc) {
            payload.state_register_que.location_id = loc.location_id;
            payload.state_register_que.company_location_mapping_id = loc.company_location_mapping_id;
            payload.state_register_que.state_id = loc.state ? loc.state.id : undefined;
          }
        }
        if (filters.employer) payload.state_register_que.employer_id = filters.employer;
        if (filters.employerLocation) payload.state_register_que.employer_location_id = filters.employerLocation;
        if (filters.subContractor) payload.state_register_que.sub_contractor_id = filters.subContractor;
        if (filters.subContractorLocation) payload.state_register_que.sub_contractor_location_id = filters.subContractorLocation;

        // If the availability check already found that every matching
        // Company/Employer/Sub-Contractor combination for this exact
        // selection has no data, silently ask for Nil register(s) instead of
        // failing.
        const clraCheckedFor = [
          filters.company, filters.associateCompany, filters.year, filters.month,
          filters.locations[0] || "", filters.employer, filters.employerLocation,
          filters.subContractor, filters.subContractorLocation,
        ].join("|");
        const isClraNil = !!(
          clraAvailabilityResult &&
          clraAvailabilityResult.checkedFor === clraCheckedFor &&
          !clraAvailabilityResult.hasData
        );
        // Narrower case: the "no Sub Contractor" combination specifically has
        // no data (while sibling combinations that do have a Sub Contractor
        // are left untouched) — scope the request to just that row.
        const isClraNoSubNil = !!(
          clraNoSubAvailabilityResult &&
          clraNoSubAvailabilityResult.checkedFor === clraCheckedFor &&
          !clraNoSubAvailabilityResult.hasData
        );
        if (isClraNil || isClraNoSubNil) {
          payload.state_register_que.generate_nil_register = true;
        }
        if (isClraNoSubNil) {
          payload.state_register_que.no_sub_contractor = true;
        }

        const resultAction = await dispatch(addStateRegisterQue({ payload, establishmentType }));
        if (addStateRegisterQue.fulfilled.match(resultAction)) {
          const data = resultAction.payload.data || {};
          const message = data.message || "Queue creation started.";
          if (!(isClraNil || isClraNoSubNil) && isZeroCreatedMessage(data)) {
            toast.error(NO_DATA_FRIENDLY_MESSAGE_CLRA);
          } else {
            toast.success((isClraNil || isClraNoSubNil) ? `Nil Register — ${message}` : message);
          }
          fetchData();
        } else {
          toast.error(getFriendlyErrorMessage(resultAction.payload, true));
        }
        setDownloading(false);
        return;
      }

      // ── Shops/Factory ──
      if (filters.locations.length === 0) {
        // Case 1 — no location selected: backend auto-expands to every
        // location assigned under this company/associate company/state.
        // Always async now, regardless of how many locations that resolves
        // to — no counts up front, full breakdown goes out by email.
        const payload = {
          state_register_que: {
            company_id: filters.company,
            associate_company_id: filters.associateCompany,
            state_id: filters.stateName,
            year: filters.year,
            month: filters.month,
            establishment_type: establishmentType,
          },
        };
        const resultAction = await dispatch(addStateRegisterQue({ payload, establishmentType }));
        if (addStateRegisterQue.fulfilled.match(resultAction)) {
          const data = resultAction.payload.data || {};
          toast.success(data.message || "Queue creation started in the background — you'll receive an email with the results shortly.");
          fetchData();
        } else {
          toast.error(getFriendlyErrorMessage(resultAction.payload));
        }
      } else if (filters.locations.length === 1) {
        // Case 2 — exactly one location: standard single-queue create. If the
        // data-availability check already found no data for this exact
        // selection, silently ask for a Nil register instead of failing.
        const locationId = filters.locations[0];
        const checkedFor = [
          filters.company, filters.associateCompany, filters.stateName,
          locationId, establishmentType, filters.year, filters.month,
        ].join("|");
        const shouldGenerateNil = !!(
          availabilityResult &&
          availabilityResult.checkedFor === checkedFor &&
          !availabilityResult.hasData
        );
        const payload: any = {
          state_register_que: {
            company_id: filters.company,
            associate_company_id: filters.associateCompany,
            location_id: locationId,
            state_id: filters.stateName,
            year: filters.year,
            month: filters.month,
            establishment_type: establishmentType,
          },
        };
        if (shouldGenerateNil) {
          payload.state_register_que.generate_nil_register = true;
        }
        const resultAction = await dispatch(addStateRegisterQue({ payload, establishmentType }));
        if (addStateRegisterQue.fulfilled.match(resultAction)) {
          const data = resultAction.payload.data || {};
          const nilPrefix = shouldGenerateNil ? "Nil Register — " : "";
          if (data.que_id) {
            toast.success(`${nilPrefix}Que Number ${data.que_id} received.`);
          } else {
            toast.success(`${nilPrefix}${data.message || "Register queue created successfully."}`);
          }
          fetchData();
        } else {
          toast.error(getFriendlyErrorMessage(resultAction.payload));
        }
      } else {
        // Case 3 — multiple locations: one batched request with location_ids.
        // Backend decides sync (≤50 — created/skipped counts) vs async
        // (51+ — background + email) itself; either way we just show its message.
        const payload = {
          state_register_que: {
            company_id: filters.company,
            associate_company_id: filters.associateCompany,
            state_id: filters.stateName,
            year: filters.year,
            month: filters.month,
            establishment_type: establishmentType,
            location_ids: filters.locations,
          },
        };
        const resultAction = await dispatch(addStateRegisterQue({ payload, establishmentType }));
        if (addStateRegisterQue.fulfilled.match(resultAction)) {
          const data = resultAction.payload.data || {};
          toast.success(data.message || "Queue creation started.");
          fetchData();
        } else {
          toast.error(getFriendlyErrorMessage(resultAction.payload));
        }
      }
      setDownloading(false);
    } catch (error) {
      console.error("Error processing registers:", error);
      toast.error("An error occurred while processing the registers.");
      setDownloading(false);
    }
  };

  // Moved interfaces to utils if needed globally

  const onClickDownloadZip = (url: any) => {
    if (url) {
      downloadWithPresignedUrl(url.split("/").pop(), url);
    } else {
      toast.info("No URL found");
    }
  };

  const onclickDownloadExcel = (file: any) => {
    // Some registers don't have an excel_url at all — a docx_url is generated for
    // them instead. The Excel column falls back to that docx file rather than
    // showing nothing, since the two are mutually exclusive per register.
    const url = file.register_excel_url || file.register_docx_url || "";
    if (url) {
      downloadWithPresignedUrl(url.split("/").pop(), url);
    } else {
      toast.info("No URL found");
      return "No URL found";
    }
  };

  const onclickDownloadPdf = (file: any) => {
    const url = file.register_pdf_url || "";
    if (url) {
      downloadWithPresignedUrl(url.split("/").pop(), url);
    } else {
      toast.info("No URL found");
      return "No URL found";
    }
  };

  const onclickDownloadBoth = (each: any) => {
    setOpenDownloadModal(true);
    //dispatch(getstateRegisterQueExcelandPdfDownloadDetails(Payload))
    setSelectedStateRegisterQue(each);
    const _stateRegisters = each.state_registers || [];
    const updatedStateRegisters: any = [];
    _stateRegisters.forEach((_stateRegister: any) => {
      const _registers = [...(_stateRegister.registers || [])].sort(
        (a: any, b: any) => Number(a.id) - Number(b.id),
      );
      if (_registers.length > 1) {
        updatedStateRegisters.push(
          ..._registers.map((register: any, index: number) => {
            return {
              ..._stateRegister,
              form_name: _stateRegister.form_name + ` Part - ${index + 1}`,
              register_excel_url: register.excel_url || "",
              register_pdf_url: register.pdf_url || "",
              register_docx_url: register.docx_url || "",
            };
          }),
        );
      } else {
        updatedStateRegisters.push({
          ..._stateRegister,
          register_excel_url: (_registers[0] || {}).excel_url || "",
          register_pdf_url: (_registers[0] || {}).pdf_url || "",
          register_docx_url: (_registers[0] || {}).docx_url || "",
        });
      }
    });
    setStateRegisters(updatedStateRegisters);
  };

  // Using formatDate from utils

  const handleChangeSearchInput = (event: any) => {
    updateSearchConfig({ searchInput: event.target.value });
  };

  const onClickClearSearch = () => {
    setIsSearchActive(false);
    updateSearchConfig({ searchInput: "" });
    fetchData();
  };

  const onClickSearch = () => {
    setIsSearchActive(true);
    updateSearchConfig({ page: 0 });
    fetchWithSearch(searchConfig.searchInput);
  };

  const subContractorOptions = employerSubContractors.map((x: any) => ({ label: x.name, value: x.id }));
  const subContractorLocationOptions = employerSubContractorsLocations.map((x: any) => ({ label: x.location_name, value: x.id }));
  const selectedSubContractor = subContractorOptions.find((o) => o.value === filters.subContractor) || null;
  const selectedSubContractorLocation = subContractorLocationOptions.find((o) => o.value === filters.subContractorLocation) || null;

  return (
    <>
    <div className="performerclient-wrapper h-100">
      <div className="performer-client-page h-100 py-3">
      {/* Download Modal */}
      <DownloadModal
        open={openDownloadModal}
        onClose={() => setOpenDownloadModal(false)}
        stateRegisters={stateRegisters}
        selectedStateRegisterQue={selectedStateRegisterQue}
        onclickDownloadExcel={onclickDownloadExcel}
        onclickDownloadPdf={onclickDownloadPdf}
        onClickDownloadZip={onClickDownloadZip}
      />
      <div>
        <div>
              <div className="pageHeading mb-0" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h4 className="mb-0">Register Generation</h4>
                <EmployeeUploadSearch
                  value={searchConfig.searchInput}
                  placeholder="Search Que No"
                  onChange={handleChangeSearchInput}
                  onSearch={onClickSearch}
                  onClear={onClickClearSearch}
                />
              </div>
              {/* Filter Box*/}
              <div className="filters" style={{ padding: "16px 20px" }}>
                <div
                  style={{
                    backgroundColor: "#ffffff",
                    padding: "16px 16px",
                    borderRadius: "8px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "24px",
                      alignItems: "flex-end",
                    }}
                  >
                    <div style={{ minWidth: "250px" }}>
                      <label className="filter-label"><small>Establishment Type</small></label>
                      <Select
                        options={establishmentTypeOptions}
                        value={selectedEstablishmentType}
                        onChange={handleChangeEstablishmentType}
                        className="select-control"
                        components={{ DropdownIndicator: PerformerDropdownIndicator }}
                      />
                    </div>

                    {showDashboardDetails && (
                      <div>
                        <div>
                          {filters.establishmentType !== "CLRA" &&
                            hasUserAccess(
                              USER_PRIVILEGES.VIEW_PERFORMER_CLIENT_REGISTER_QUE,
                            ) && (
                              <button className="button-primary"
                                onClick={handleProcessRegisters}
                                disabled={
                                  !filters.company ||
                                  !filters.associateCompany ||
                                  !filters.stateName ||
                                  !filters.year ||
                                  !filters.month ||
                                  !filters.establishmentType ||
                                  checkingAvailability
                                }
                              >
                                Generate Registers
                              </button>
                            )}
                          {filters.establishmentType === "CLRA" &&
                            hasUserAccess(
                              USER_PRIVILEGES.VIEW_PERFORMER_CLIENT_REGISTER_QUE,
                            ) && (
                              <button className="button-primary"
                                onClick={handleProcessRegisters}
                                disabled={
                                  !filters.company ||
                                  !filters.associateCompany ||
                                  !filters.year ||
                                  !filters.month ||
                                  !filters.establishmentType ||
                                  checkingClraAvailability ||
                                  checkingClraNoSubAvailability
                                }
                              >
                                Generate Registers
                              </button>
                            )}
                        </div>
                      </div>
                    )}
                  </div>

                  {filters.locations.length === 1 &&
                    availabilityResult &&
                    !availabilityResult.hasData && (
                      <div style={{ marginBottom: "16px", textAlign: "right", fontSize: "12px", color: "#b45309" }}>
                        {availabilityResult.errors.length
                          ? availabilityResult.errors.join(", ")
                          : "No data available for this location for the selected period."}
                        {" "}Generate Registers will create a Nil register.
                      </div>
                    )}

                  {filters.establishmentType === "CLRA" &&
                    clraAvailabilityResult &&
                    !clraAvailabilityResult.hasData && (
                      <div style={{ marginBottom: "16px", textAlign: "right", fontSize: "12px", color: "#b45309" }}>
                        {clraAvailabilityResult.errors.length
                          ? clraAvailabilityResult.errors.join(", ")
                          : "No data available for the selected combination(s)."}
                        {" "}Generate Registers will create{" "}
                        {clraAvailabilityResult.combinationCount > 1
                          ? `${clraAvailabilityResult.combinationCount} Nil registers`
                          : "a Nil register"}.
                      </div>
                    )}

                  {filters.establishmentType === "CLRA" &&
                    clraNoSubAvailabilityResult &&
                    !clraNoSubAvailabilityResult.hasData && (
                      <div style={{ marginBottom: "16px", textAlign: "right", fontSize: "12px", color: "#b45309" }}>
                        {clraNoSubAvailabilityResult.errors.length
                          ? clraNoSubAvailabilityResult.errors.join(", ")
                          : "No data available for this combination without a Sub Contractor."}
                        {" "}Generate Registers will create a Nil register for this combination only —{" "}
                        other Sub Contractor combinations under this Employer are unaffected.
                      </div>
                    )}

                  <div style={{ display: "flex", gap: "12px" }}>
                    <div className="filter-subcontainer">
                      <label className="filter-label"><small>Company</small></label>
                      <Select
                        options={companyOptions}
                        value={selectedCompany}
                        onChange={(option: any) => { if (option) handleChangeCompany(option); }}
                        isClearable={false}
                        className="select-control"
                        components={{ DropdownIndicator: PerformerDropdownIndicator }}
                      />
                    </div>

                    <div className="filter-subcontainer">
                      <label className="filter-label"><small>Associate Company</small></label>
                      <Select
                        options={associateCompanyOptions}
                        value={selectedAssociateCompany}
                        onChange={handleChangeAssociateCompany}
                        isDisabled={!filters.company}
                        className="select-control"
                        components={{ DropdownIndicator: PerformerDropdownIndicator }}
                      />
                    </div>

                    {filters.establishmentType !== "CLRA" && (
                      <>
                        <div className="filter-subcontainer">
                          <label className="filter-label"><small>State</small></label>
                          <Select
                                options={stateOptions}
                            value={selectedStateName}
                            onChange={handleChangeStateName}
                            isDisabled={!filters.associateCompany}
                            className="select-control"
                            components={{ DropdownIndicator: PerformerDropdownIndicator }}
                          />
                        </div>
                        <div className="filter-subcontainer">
                          <label className="filter-label">
                            <small>Location</small>
                          </label>
                          <Select
                                options={nonClraLocationOptions}
                            value={selectedLocationsMulti}
                            onChange={handleChangeLocationMulti}
                            isDisabled={!filters.stateName}
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            placeholder="All"
                            className="select-control"
                            components={{ DropdownIndicator: PerformerDropdownIndicator }}
                            theme={(baseTheme: any) => ({
                              ...baseTheme,
                              colors: {
                                ...baseTheme.colors,
                                primary: "#2965AD",
                                primary75: "rgba(41, 101, 173, 0.75)",
                                primary50: "rgba(41, 101, 173, 0.50)",
                                primary25: "rgba(41, 101, 173, 0.25)",
                              },
                            })}
                            styles={{
                              multiValueRemove: (base: any) => ({
                                ...base,
                                ':hover': {
                                  backgroundColor: 'transparent',
                                  color: 'inherit',
                                },
                              }),
                            }}
                          />
                          {filters.stateName && filters.locations.length === 0 && (
                            <small className="text-muted" style={{ display: "block", marginTop: "4px" }}>
                              No location selected — registers will be generated for all locations in this state.
                            </small>
                          )}
                        </div>
                      </>
                    )}

                    {filters.establishmentType === "CLRA" && (
                      <>
                        <div className="filter-subcontainer">
                          <label className="filter-label"><small>Company Location</small></label>
                          <Select
                                options={clraLocationOptions}
                            value={selectedLocationSingle}
                            onChange={handleChangeLocationSingle}
                            isDisabled={!filters.associateCompany}
                            placeholder="All"
                            className="select-control"
                            components={{ DropdownIndicator: PerformerDropdownIndicator }}
                          />
                          {filters.associateCompany && filters.locations.length === 0 && (
                            <small className="text-muted" style={{ display: "block", marginTop: "4px" }}>
                              No location selected — registers will be generated for all Company/Employer/Sub-Contractor mappings under this associate company.
                            </small>
                          )}
                        </div>
                        <div className="filter-subcontainer">
                          <label className="filter-label"><small>Employer</small></label>
                          <Select
                                options={employerOptions}
                            value={selectedEmployer}
                            onChange={handleChangeEmployer}
                            isDisabled={filters.locations.length === 0}
                            className="select-control"
                            components={{ DropdownIndicator: PerformerDropdownIndicator }}
                          />
                        </div>
                        <div className="filter-subcontainer">
                          <label className="filter-label"><small>Employer Location</small></label>
                          <Select
                                options={employerLocationOptions}
                            value={selectedEmployerLocation}
                            onChange={handleChangeEmployerLocation}
                            isDisabled={!filters.employer}
                            className="select-control"
                            components={{ DropdownIndicator: PerformerDropdownIndicator }}
                          />
                        </div>
                      </>
                    )}

                    <div className="filter-subcontainer">
                      <label className="filter-label"><small>Year</small></label>
                      <Select
                        options={yearOptions}
                        value={selectedYear}
                        onChange={handleChangeYear}
                        isDisabled={
                          filters.establishmentType === "CLRA"
                            ? !filters.associateCompany
                            : !filters.stateName
                        }
                        className="select-control"
                        components={{ DropdownIndicator: PerformerDropdownIndicator }}
                      />
                    </div>

                    <div className="filter-subcontainer">
                      <label className="filter-label"><small>Month</small></label>
                      <Select
                        options={monthOptions}
                        value={selectedMonth}
                        onChange={handleChangeMonth}
                        isDisabled={!filters.year}
                        className="select-control"
                        components={{ DropdownIndicator: PerformerDropdownIndicator }}
                      />
                    </div>

                    {filters.establishmentType === "CLRA" && (
                      <>
                        <div className="d-flex align-items-end">
                          <div className="d-flex flex-column">
                            <button
                              type="submit"
                              className="button-primary"
                              onClick={(e) => {
                                e.preventDefault();
                                if (!filters.employer || !filters.employerLocation) {
                                  return toast.error("Please select Employer and Employer Location");
                                }
                                setOpenMoreFilters(true);
                              }}
                              style={{ width: "136px" }}
                            >
                              <span className="ms-2">More Filters</span>
                            </button>
                          </div>
                        </div>

                        <Modal
                          show={openMoreFilters}
                          onHide={() => setOpenMoreFilters(false)}
                          backdrop="static"
                          animation={false}
                          centered
                          size="lg"
                          className="performer-filters-modal"
                        >
                          <Modal.Header closeButton={true}>
                            <Modal.Title className="bg d-flex align-items-center justify-content-between w-100">
                              <div className="fs-5 fw-semibold">More Filters</div>
                              <div>
                                <IoMdClose
                                  onClick={() => setOpenMoreFilters(false)}
                                  style={{ cursor: "pointer", marginRight: "8px", verticalAlign: "middle" }}
                                />
                              </div>
                            </Modal.Title>
                          </Modal.Header>
                          <Modal.Body>
                            <div className="d-flex justify-content-start">
                              <div className="col-12">
                                <div className="row">
                                  <div className="col-12">
                                    <div className="form-group performer-form-field">
                                      <label className="form-label text-sm">Sub Contractor</label>
                                      <div className="input-group">
                                        <Select
                                          placeholder="Select Sub Contractor"
                                          options={subContractorOptions}
                                          value={selectedSubContractor}
                                          onChange={handleChangeSubContractor}
                                          isDisabled={!filters.employerLocation}
                                          className="select-control w-100"
                                          menuPosition="fixed"
                                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                        />
                                      </div>
                                    </div>
                                    <div className="form-group performer-form-field">
                                      <label className="form-label text-sm">Sub Contractor Location</label>
                                      <div className="input-group">
                                        <Select
                                          placeholder="Select Sub Contractor Location"
                                          options={subContractorLocationOptions}
                                          value={selectedSubContractorLocation}
                                          onChange={handleChangeSubContractorLocation}
                                          isDisabled={!filters.subContractor}
                                          className="select-control w-100"
                                          menuPosition="fixed"
                                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Modal.Body>
                          <Modal.Footer className="d-flex gap-2 flex-nowrap">
                            <button className="button-secondary m-0 w-50" onClick={clearMoreFilters}>
                              Clear All
                            </button>
                            <button className="button-primary m-0 w-50" onClick={applyMoreFilters}>
                              Submit
                            </button>
                          </Modal.Footer>
                        </Modal>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              <div style={{ padding: "0 20px" }}>
                <PerformerTable
                  columns={[
                    { label: "Company", sortKey: "company_id", render: (row) => (row.company && row.company.name) ? row.company.name : "NA" },
                    { label: "AssociateCompany", sortKey: "associate_company_id", render: (row) => (row.associate_company && row.associate_company.name) ? row.associate_company.name : "NA" },
                    { label: "State", render: (row) => (row.state && row.state.name) ? row.state.name : "NA" },
                    { label: "Location", sortKey: "location_id", render: (row) => (row.location && row.location.name) ? row.location.name : "NA" },
                    ...(showCLRAColumns ? [
                      { label: "Employer", render: (row: any) => (row.employer && row.employer.name) ? row.employer.name : "NA" },
                      { label: "Employer Location", render: (row: any) => (row.employer_location && row.employer_location.location_name) ? row.employer_location.location_name : "NA" },
                      { label: "Sub Contractor", render: (row: any) => (row.sub_contractor && row.sub_contractor.name) ? row.sub_contractor.name : "NA" },
                      { label: "Sub Contractor Location", render: (row: any) => (row.sub_contractor_location && row.sub_contractor_location.location_name) ? row.sub_contractor_location.location_name : "NA" },
                    ] : []),
                    { label: "Year", sortKey: "year", render: (row) => row.year || "NA" },
                    { label: "Month", render: (row) => row.month || "NA" },
                    { label: "Que Id", render: (row) => row.que_id || "NA" },
                    { label: "Que Status", render: (row) => row.que_status ? (QUE_STATUS_DISPLAY[row.que_status] || row.que_status) : "NA" },
                    {
                      label: "Download",
                      render: (row: any) => (
                        <div style={{ display: "flex", justifyContent: "center" }}>
                          {row.que_status === "pdf_processing_partially_completed" || row.que_status === "pdf_processing_completed" ? (
                            <Icon style={{ color: "#0654AD", opacity: 1, pointerEvents: "auto" }} type="button" name={"download"} text={"download"} action={() => onclickDownloadBoth(row)} />
                          ) : (
                            <Icon style={{ color: "#0654AD", opacity: 0.5, pointerEvents: "none" }} name={"download"} text={"download"} />
                          )}
                        </div>
                      ),
                    },
                    { label: "Created Date", render: (row) => formatDate(row.created_at) || "NA" },
                    { label: "Last Updated", render: (row) => formatDate(row.completed_date) || "NA" },
                  ]}
                  data={stateRegisterQue || []}
                  rowKey={(row) => row.que_id || row._id}
                  count={stateRegisterQueCount}
                  page={searchConfig.page}
                  rowsPerPage={searchConfig.rowsPerPage}
                  activeSort={searchConfig.activeSort}
                  sortType={searchConfig.sortType}
                  onSort={handleSort}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </div>
            </div>
        </div>
      </div>
    </div>
    {loading && <PageLoader>Loading...</PageLoader>}
    {downloading && <PageLoader>Processing Registers...</PageLoader>}
    </>
  );
};
export default RegistersQueue;
